import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { post } from "@/lib/api";
import type { ClientOption } from "@/lib/clients";
import { eur } from "@/lib/format";
import { evaluate, linesFromRow, newLine, sharedPayee, type ShareLine } from "@/lib/shareLines";
import type { ClassifyOutput, ExpenseRow } from "@/lib/types";
import { ShareLinesEditor } from "./ShareLinesEditor";

export function ClassifyDialog({ rows, clients, defaultMarginPct, onClose }: {
  rows: ExpenseRow[];
  /** Includes former clients; a line offers them only when it already points at one. */
  clients: ClientOption[];
  defaultMarginPct: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const single = rows.length === 1 ? rows[0] : null;
  const [mode, setMode] = useState<"customer" | "general">("customer");
  const [lines, setLines] = useState<ShareLine[]>([newLine()]);
  const [note, setNote] = useState("");
  const [rule, setRule] = useState(false);
  useEffect(() => {
    setMode(single?.classification === "general" ? "general" : "customer");
    setLines(single ? linesFromRow(single) : [newLine()]);
    setNote(single?.note ?? "");
    setRule(false);
  }, [rows]);

  const payee = sharedPayee(rows);
  const ev = evaluate(lines, single?.amount ?? null, defaultMarginPct);
  const total = rows.reduce((s, r) => s + r.amount, 0);

  const save = useMutation({
    mutationFn: () => {
      const ids = rows.map((r) => r.sevdesk_transaction_id);
      const base = { ids, ...(note.trim() ? { note: note.trim() } : {}), rule: rule && payee !== null };
      return mode === "customer"
        ? post<ClassifyOutput>("/api/expenses/assign", { ...base, shares: ev.specs })
        : post<ClassifyOutput>("/api/expenses/general", base);
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["unbilled"] });
      qc.invalidateQueries({ queryKey: ["rules"] });
      toast.success(res.autoClassified > 0 ? `Zugeordnet · ${res.autoClassified} weitere per Regel` : "Zugeordnet");
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={rows.length > 0} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader><DialogTitle>{single ? "Ausgabe zuordnen" : `${rows.length} Ausgaben zuordnen`}</DialogTitle></DialogHeader>
        <form className="contents" onSubmit={(e) => { e.preventDefault(); if (!save.isPending && (mode === "general" || ev.error === null)) save.mutate(); }}>
        <p className="text-sm text-muted-foreground">
          {single ? `${single.payee} · ${eur(single.amount)}` : `Summe ${eur(total)} — „Rest“ wird je Ausgabe berechnet`}
        </p>
        <Tabs value={mode} onValueChange={(v) => setMode(v as "customer" | "general")}>
          <TabsList><TabsTrigger value="customer">Kunden</TabsTrigger><TabsTrigger value="general">Allgemein</TabsTrigger></TabsList>
        </Tabs>

        {mode === "customer" ? (
          <div className="space-y-2">
            <ShareLinesEditor lines={lines} onChange={setLines} clients={clients} defaultMarginPct={defaultMarginPct} />
            <p className={ev.error ? "text-sm text-destructive" : "text-sm text-muted-foreground tabular-nums"}>
              {ev.error ?? [
                `Kunden ${eur(ev.fixedSum + (ev.restAmount ?? 0))}`,
                ev.generalAmount !== null ? `Allgemein ${eur(ev.generalAmount)}` : null,
                ev.rebillTotal !== null ? `Weiterverrechnet ${eur(ev.rebillTotal)}` : null,
              ].filter(Boolean).join(" · ")}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Eigene Betriebskosten, werden keinem Kunden weiterverrechnet.</p>
        )}

        <div className="space-y-1.5"><Label htmlFor="note">Notiz (optional)</Label><Input id="note" value={note} onChange={(e) => setNote(e.target.value)} /></div>
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={rule} disabled={payee === null} onCheckedChange={(v) => setRule(!!v)} />
          <span>
            {payee ? `Regel für „${payee}“ merken` : "Regel merken"}
            <span className="block text-xs text-muted-foreground">
              {payee === null
                ? "Nur möglich, wenn alle gewählten Ausgaben denselben Empfänger haben."
                : "Künftige und noch offene Ausgaben dieses Empfängers werden gleich aufgeteilt; eine „Rest“-Zeile bleibt „Rest“. Rechnungs-Nr. werden nicht übernommen; die setzt man unter „Regeln“."}
            </span>
          </span>
        </label>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button disabled={save.isPending || (mode === "customer" && ev.error !== null)} type="submit">Zuordnen</Button>
        </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
